/** A photo group as the order endpoint returns it. */
export interface RestoredGroup {
  id?: string;
  total_n_of_photos?: number;
  totalNOfPhotos?: number;
  photos?: ({ count?: number } | null)[] | null;
}

export interface RestoredOrder {
  totalNOfPhotos?: number;
  orderPhotos?: (RestoredGroup | null)[];
}

/** Photos a group really holds, counting duplicates by `count`, not by rows. */
export function actualPhotoCount(group: RestoredGroup | null): number {
  return (group?.photos ?? []).reduce<number>(
    (sum, photo) => sum + (photo?.count ?? 1),
    0,
  );
}

/**
 * Does this restored order's cached photo count understate what it actually
 * holds?
 *
 * The rule: the cart must never show a number that contradicts the order. The
 * funnel report states it as "PHOTOS EXIST BUT ORDER REPORTS ZERO".
 *
 * WHY NOT `cachedTotal === 0`
 *
 * That was the first version and it was wrong. It only caught a cart showing
 * ZERO, so it missed the commoner shape: a customer who already had photos and
 * whose NEXT upload was interrupted. Observed on stage — an order cached at 6
 * while holding 8, with the new group stuck at `total_n_of_photos = 0` and
 * `price = 0.00`. The cart listed "2 Fotografije · 0,00 €" and no reconcile
 * fired, because 6 is not 0.
 *
 * WHY STRICTLY GREATER, NOT MERELY DIFFERENT
 *
 * Firing whenever the numbers differ would be a trap. If the order endpoint ever
 * returned a subset of photos — a limit, a projection, a future pagination — then
 * `actual` would read lower than `cached`, the predicate would fire, the
 * recalculation would not change anything, and every page load would repeat it
 * forever.
 *
 * Undercounting is the only direction that means "photos landed but were never
 * counted", which is the condition being fixed. An overcount is left alone: it
 * shows the customer too many rather than too few, and it is the pre-existing
 * behaviour rather than a new loop.
 *
 * Counting uses `photo.count`, not `photos.length`. A photo ordered in duplicate
 * carries count > 1, and using the row count would make this fire permanently on
 * every such order — 640 of them exist in production.
 *
 * Pure and separate from the context so it can be asserted directly; the restore
 * effect lives in a 600-line provider with no test harness, which is how the
 * first version's gap survived.
 */
export function shouldReconcileRestoredOrder(
  order: RestoredOrder | null | undefined,
): boolean {
  const cachedTotal = order?.totalNOfPhotos ?? 0;
  const actualTotal = (order?.orderPhotos ?? []).reduce<number>(
    (sum, group) => sum + actualPhotoCount(group),
    0,
  );
  return actualTotal > cachedTotal;
}

/**
 * The groups whose own cached count understates what they hold — i.e. the ones
 * an interrupted upload left behind.
 *
 * The notice names these specifically. Showing every group with photos would
 * tell a customer "we saved 8" when 6 of those were already theirs from an
 * earlier session and only 2 were rescued.
 */
export function rescuedGroupIds(
  order: RestoredOrder | null | undefined,
): string[] {
  // Null-safe deliberately. The caller renders before its own order query has
  // resolved -- the provider flags the reconcile from a DIFFERENT request -- so
  // `undefined` here is a normal first render, not a bug. An earlier version
  // dereferenced it and crashed the upload page for exactly the customer this
  // feature exists for.
  return (order?.orderPhotos ?? [])
    .filter((group) => {
      const cached = group?.total_n_of_photos ?? group?.totalNOfPhotos ?? 0;
      return actualPhotoCount(group) > cached;
    })
    .map((group) => group?.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}
